import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { BuildingIcon, CircleDollarSignIcon, LandmarkIcon, PercentIcon, TruckIcon, WalletIcon } from 'lucide-react';
import { brandEconomics, cashPosition, costStructure, leaseAccounts, monthlyFinancials, partnerAccounts } from '../data/financeData';
const currency = (value: number) => `$${Math.round(value).toLocaleString('en-US')}`;
const compact = (value: number) => value >= 1_000_000 ? `$${(value / 1_000_000).toFixed(2)}M` : `$${Math.round(value / 1000)}K`;
type Period = 'Quarter to date' | 'Year to date';
export function FinanceDashboard() {
  const [period, setPeriod] = useState<Period>('Year to date');
  const months = period === 'Year to date' ? monthlyFinancials : monthlyFinancials.slice(-3);
  const totals = useMemo(() => {
    const grossRecovery = months.reduce((sum, month) => sum + month.grossRecovery, 0);
    const netRevenue = months.reduce((sum, month) => sum + month.netRevenue, 0);
    const operatingCosts = months.reduce((sum, month) => sum + month.operatingCosts, 0);
    const payouts = months.reduce((sum, month) => sum + month.brandPayouts, 0);
    return {
      grossRecovery,
      netRevenue,
      operatingCosts,
      payouts,
      operatingIncome: netRevenue - operatingCosts,
      takeRate: grossRecovery > 0 ? netRevenue / grossRecovery * 100 : 0
    };
  }, [months]);
  const chartData = months.map(month => ({
    ...month,
    margin: Number(((month.netRevenue - month.operatingCosts) / month.netRevenue * 100).toFixed(1))
  }));
  const costTotal = costStructure.reduce((sum, line) => sum + line.amount, 0);
  const partnerOutstanding = partnerAccounts.reduce((sum, account) => sum + account.outstanding, 0);
  const rentTotal = leaseAccounts.reduce((sum, lease) => sum + lease.monthlyRent, 0);
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="finance-heading" data-id="element-1125" data-mp="1141axnhazznd">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-1126" data-mp="24eowm05zhwdy">
        <div data-id="element-1127" data-mp="1zfug54vhojx0">
          <span className="inline-flex items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1128" data-mp="1zg45cq8pmn1n">
            <LandmarkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1129" data-mp="2dchftfetto6m" />
            Finance
          </span>
          <h1 id="finance-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1130" data-mp="28htx5x6i7nzy">
            Company financials
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-ink/60" data-id="element-1131" data-mp="2icvjymdb4dzd">
            Consolidated performance across every brand, recovery channel, 3PL partner, and facility.
          </p>
        </div>
        <div className="flex gap-1 self-start rounded-xl border border-ink/10 bg-cream p-1 sm:self-auto" data-id="element-1132" data-mp="2s4003pepy4vs">
          {(['Quarter to date', 'Year to date'] as const).map(item => <button key={item} type="button" onClick={() => setPeriod(item)} aria-pressed={period === item} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${period === item ? 'bg-[#BB5E40] text-cream' : 'text-ink/60 hover:text-ink'}`} data-id="element-1133" data-mp="3157q7k72zzes">
              {item}
            </button>)}
        </div>
      </div>

      <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Financial summary" data-id="element-1134" data-mp="31zgqkwbjyypj">
        <HeadlineMetric icon={CircleDollarSignIcon} label="Gross recovery" value={compact(totals.grossRecovery)} detail={`${currency(totals.payouts)} paid to brands`} data-id="element-1135" data-mp="36px40gfdikbw" />
        <HeadlineMetric icon={WalletIcon} label="Net revenue" value={compact(totals.netRevenue)} detail="ManyCo share after brand payouts" data-id="element-1136" data-mp="2wzkletr4d18e" />
        <HeadlineMetric icon={PercentIcon} label="Blended take rate" value={`${totals.takeRate.toFixed(1)}%`} detail="Net revenue over gross recovery" data-id="element-1137" data-mp="2n2po2e6l16vp" />
        <HeadlineMetric icon={LandmarkIcon} label="Operating income" value={compact(totals.operatingIncome)} detail={`${currency(totals.operatingCosts)} in operating costs`} positive={totals.operatingIncome > 0} data-id="element-1138" data-mp="2azd9px24ehzg" />
      </section>

      <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-label="Revenue and margin" data-id="element-1139" data-mp="1x88hhg130rz5">
        <h2 className="text-lg font-semibold" data-id="element-1140" data-mp="1s8dp181nrubz">Revenue and operating margin</h2>
        <p className="mt-1 text-sm text-ink/60" data-id="element-1141" data-mp="1n9smv32uj0kw">Net revenue against operating costs, with margin plotted on top.</p>
        <div className="mt-6 h-72" data-id="element-1142" data-mp="1icaou7qda5ro">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1143" data-mp="1id2c8v9t8x6v">
            <ComposedChart data={chartData} margin={{
            top: 8,
            right: 8,
            left: -15,
            bottom: 0
          }} barCategoryGap="24%" barGap={6} data-id="element-1144" data-mp="3holfqucchd95">
              <CartesianGrid vertical={false} stroke="#1C1A17" strokeOpacity={0.1} strokeDasharray="3 5" data-id="element-1145" data-mp="3cq1uyx9hm7id" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} dy={8} data-id="element-1146" data-mp="3l60x13oq1g9n" />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} tickFormatter={(value: number) => `$${Math.round(value / 1000)}k`} data-id="element-1147" data-mp="3tykwu7gr1qw2" />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} tickFormatter={(value: number) => `${value}%`} data-id="element-1148" data-mp="3297yot0p5p8g" />
              <Tooltip content={<FinanceTooltip data-id="element-1150" data-mp="3g53egpzsuk7m" />} cursor={{
              fill: '#BB5E40',
              fillOpacity: 0.06
            }} data-id="element-1149" data-mp="3g8r77mxf72la" />
              <Bar yAxisId="left" dataKey="netRevenue" name="Net revenue" fill="#BB5E40" radius={[6, 6, 0, 0]} maxBarSize={54} data-id="element-1151" data-mp="qhm8vma6ticb" />
              <Bar yAxisId="left" dataKey="operatingCosts" name="Operating costs" fill="#C9BFAE" radius={[6, 6, 0, 0]} maxBarSize={54} data-id="element-1152" data-mp="5stf1676p2zj" />
              <Line yAxisId="right" type="monotone" dataKey="margin" name="Operating margin" stroke="#2E7D51" strokeWidth={2.5} dot={{
              r: 3,
              fill: '#2E7D51'
            }} data-id="element-1153" data-mp="ui8b4c6r8lj" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-ink/60" data-id="element-1154" data-mp="aso557j4xojj">
          <Legend color="#BB5E40" label="Net revenue" data-id="element-1155" data-mp="3j8rdlfi2nh8x" />
          <Legend color="#C9BFAE" label="Operating costs" data-id="element-1156" data-mp="3e8mx3ph5ymip" />
          <Legend color="#2E7D51" label="Operating margin" data-id="element-1157" data-mp="3ea6ejjek33m2" />
        </div>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]" data-id="element-1158" data-mp="3syawum9p5d4b">
        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="brand-economics-heading" data-id="element-1159" data-mp="3o6j548et11cl">
          <h2 id="brand-economics-heading" className="text-base font-semibold" data-id="element-1160" data-mp="30fnirqt4aycq">Brand economics</h2>
          <p className="mt-1 text-sm text-ink/60" data-id="element-1161" data-mp="2vhly51g1mqz5">Where recovery revenue is generated and what it contributes.</p>
          <div className="mt-5 -mx-2 overflow-x-auto px-2" data-id="element-1162" data-mp="1ay012g7e2csm">
            <table className="w-full min-w-[620px] text-left text-xs" data-id="element-1163" data-mp="1frutc812803b">
              <thead className="border-b border-ink/10 text-ink/50" data-id="element-1164" data-mp="1126t7a4m77h6">
                <tr data-id="element-1165" data-mp="10zu2o8ksjosi">
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1166" data-mp="160zxcby0hm4o">Brand</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1167" data-mp="15yx83u4onx10">Gross recovery</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1168" data-mp="1tohrge4r0wat">Brand payout</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1169" data-mp="1yovzvxa9i89d">Net revenue</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1170" data-mp="1kpcwfhsebl3e">Contribution</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1171" data-mp="1opwow820y0j7">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/8" data-id="element-1172" data-mp="9d3mzot63n1w">
                {brandEconomics.map(brand => <tr key={brand.brand} data-id="element-1173" data-mp="14zvyq5my57sh">
                    <th scope="row" className="py-3 pr-3 font-semibold text-ink" data-id="element-1174" data-mp="150yna6wsqy58">{brand.brand}</th>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1175" data-mp="zzrfw8ykg0z3">{currency(brand.grossRecovery)}</td>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1176" data-mp="w00j9omcyqmn">{currency(brand.brandPayout)}</td>
                    <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-1177" data-mp="r1pbrkjjcs7l">{currency(brand.netRevenue)}</td>
                    <td className="py-3 text-right font-semibold tabular-nums text-positive" data-id="element-1178" data-mp="1ns7z4mu1hyzz">{brand.contributionMargin.toFixed(1)}%</td>
                    <td className="py-3 text-right" data-id="element-1179" data-mp="1exjxd6noazfx">
                      <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${brand.status === 'Active' ? 'bg-positive/10 text-positive' : brand.status === 'Onboarding' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : 'bg-ink/7 text-ink/55'}`} data-id="element-1180" data-mp="1evyzp862b0du">
                        {brand.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </article>

        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="cost-structure-heading" data-id="element-1181" data-mp="2nwh3dld5cgs3">
          <h2 id="cost-structure-heading" className="text-base font-semibold" data-id="element-1182" data-mp="2xwhs5mtsbp3o">Cost structure</h2>
          <p className="mt-1 text-sm text-ink/60" data-id="element-1183" data-mp="32nq2qm6upgif">{currency(costTotal)} year to date.</p>
          <ul className="mt-5 space-y-4" data-id="element-1184" data-mp="37mksk838k5c1">
            {costStructure.map(line => {
            const share = Math.round(line.amount / costTotal * 100);
            return <li key={line.category} data-id="element-1185" data-mp="3bmbfu1tk03bt">
                  <div className="flex items-baseline justify-between gap-3" data-id="element-1186" data-mp="3gmhdbymi8k1t">
                    <span className="text-xs font-semibold text-ink" data-id="element-1187" data-mp="3q87n0pxtj4bc">{line.category}</span>
                    <span className="shrink-0 text-xs font-semibold tabular-nums text-ink/70" data-id="element-1188" data-mp="3v725trvscsvg">{currency(line.amount)}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-cream" data-id="element-1189" data-mp="3zzduy84iik6">
                    <div className="h-full rounded-full bg-[#BB5E40]" style={{
                  width: `${share}%`
                }} data-id="element-1190" data-mp="8sj2cq8cmy3r" />
                  </div>
                  <p className="mt-1 text-[11px] text-ink/50" data-id="element-1191" data-mp="csjjwjqqvpf1">{line.detail} · {share}% of spend</p>
                </li>;
          })}
          </ul>
        </article>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-2" data-id="element-1192" data-mp="mrs5ao7jnvps">
        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="partner-accounts-heading" data-id="element-1193" data-mp="rqvbibz9g7zc">
          <div className="flex items-center gap-2" data-id="element-1194" data-mp="2ok6mfl29kw55">
            <TruckIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1195" data-mp="2thf4zmrbr26x" />
            <h2 id="partner-accounts-heading" className="text-base font-semibold" data-id="element-1196" data-mp="2tjjj0xixthkx">3PL partner accounts</h2>
          </div>
          <p className="mt-1 text-sm text-ink/60" data-id="element-1197" data-mp="15w3z1u5yrlxb">{currency(partnerOutstanding)} outstanding across {partnerAccounts.length} partners.</p>
          <div className="mt-5 -mx-2 overflow-x-auto px-2" data-id="element-1198" data-mp="11v21rivlxjxz">
            <table className="w-full min-w-[520px] text-left text-xs" data-id="element-1199" data-mp="wvfjuw4u3fiu">
              <thead className="border-b border-ink/10 text-ink/50" data-id="element-1200" data-mp="wtcmfr1cqk43">
                <tr data-id="element-1201" data-mp="1tkwpxc7qb3l2">
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1202" data-mp="1tjc4qd7b0wzv">Partner</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1203" data-mp="1pkcrm5bf5txc">Monthly</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1204" data-mp="1kjgq1yejijv3">Outstanding</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1205" data-mp="1fklzube8tt0y">Terms</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1206" data-mp="95v2uis8qsc7">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/8" data-id="element-1207" data-mp="44xaxehfijxi">
                {partnerAccounts.map(account => <tr key={account.partner} data-id="element-1208" data-mp="5ogrnl6dcqwp">
                    <th scope="row" className="py-3 pr-3" data-id="element-1209" data-mp="ana7vld1iw0k">
                      <span className="block font-semibold text-ink" data-id="element-1210" data-mp="ako0pmu2aiiv">{account.partner}</span>
                      <span className="text-[11px] font-normal text-ink/50" data-id="element-1211" data-mp="3ss3s1wwtjr25">{account.location}</span>
                    </th>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1212" data-mp="3j2k5ag5zjd20">{currency(account.monthlyCost)}</td>
                    <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-1213" data-mp="3nt0lg816xcs2">{account.outstanding > 0 ? currency(account.outstanding) : '—'}</td>
                    <td className="py-3 text-right tabular-nums text-ink/60" data-id="element-1214" data-mp="3a3qe55bfbevu">{account.terms}</td>
                    <td className="py-3 text-right" data-id="element-1215" data-mp="3e3qremmsdk1b">
                      <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${account.status === 'Current' ? 'bg-positive/10 text-positive' : account.status === 'Due soon' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : 'bg-[#BB5E40]/12 text-[#94452E]'}`} data-id="element-1216" data-mp="30369rtslxu9e">
                        {account.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </article>

        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="lease-accounts-heading" data-id="element-1217" data-mp="2zfqq2qing96g">
          <div className="flex items-center gap-2" data-id="element-1218" data-mp="3e4wle3ed9f92">
            <BuildingIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1219" data-mp="3961x4lxmqyhk" />
            <h2 id="lease-accounts-heading" className="text-base font-semibold" data-id="element-1220" data-mp="3n637crzsbi9p">Facilities and rent</h2>
          </div>
          <p className="mt-1 text-sm text-ink/60" data-id="element-1221" data-mp="3j5kbmmurts28">{currency(rentTotal)} per month across {leaseAccounts.length} sites.</p>
          <ul className="mt-5 divide-y divide-ink/8" data-id="element-1222" data-mp="1ghazpu9ct41p">
            {leaseAccounts.map(lease => <li key={lease.site} className="flex items-start justify-between gap-4 py-3.5 first:pt-0 last:pb-0" data-id="element-1223" data-mp="1lfvx7lx819mg">
                <div className="min-w-0" data-id="element-1224" data-mp="1q8p34d4feo04">
                  <p className="text-sm font-semibold text-ink" data-id="element-1225" data-mp="1v6qm6y1uqnbl">{lease.site}</p>
                  <p className="mt-0.5 text-xs text-ink/55" data-id="element-1226" data-mp="1v8tddleqqmhw">{lease.purpose}</p>
                  <p className="mt-0.5 text-[11px] text-ink/45" data-id="element-1227" data-mp="xl2sikws6j3r">{lease.sqft.toLocaleString('en-US')} sq ft · term ends {lease.termEnds}</p>
                </div>
                <span className="shrink-0 font-semibold tabular-nums text-ink" data-id="element-1228" data-mp="17h5yy2yo13wi">{currency(lease.monthlyRent)}</span>
              </li>)}
          </ul>

          <div className="mt-6 border-t border-ink/10 pt-5" data-id="element-1229" data-mp="2iwkz1fm68bvu">
            <h3 className="text-sm font-semibold" data-id="element-1230" data-mp="2iw1dzq1857dl">Cash position</h3>
            <dl className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-1231" data-mp="2mxekm2c55elk">
              {cashPosition.map(position => <div key={position.label} className="rounded-xl border border-ink/10 bg-cream/45 px-4 py-3" data-id="element-1232" data-mp="b9rl7d5rm1hb">
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-1233" data-mp="7aiqa4yznxk6">{position.label}</dt>
                  <dd className={`mt-1 font-display text-xl font-semibold tracking-tight ${position.tone === 'in' ? 'text-positive' : position.tone === 'out' ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-1234" data-mp="2god7kdw2gu2">
                    {currency(position.amount)}
                  </dd>
                  <p className="mt-0.5 text-[11px] text-ink/50" data-id="element-1235" data-mp="3foj0x6qj6sua">{position.detail}</p>
                </div>)}
            </dl>
          </div>
        </article>
      </section>
    </div>;
}
function HeadlineMetric({
  icon: Icon,
  label,
  value,
  detail,
  positive = false
}: {
  icon: typeof LandmarkIcon;
  label: string;
  value: string;
  detail: string;
  positive?: boolean;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-1236" data-mp="1s2y2njwnktbt">
      <Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1237" data-mp="1wtwpl2c5cpo1" />
      <p className="mt-4 text-sm text-ink/55" data-id="element-1238" data-mp="1wv7r0vi4dlbz">{label}</p>
      <p className={`mt-1 font-display text-3xl font-semibold tracking-tight ${positive ? 'text-positive' : 'text-ink'}`} data-id="element-1239" data-mp="1j3b2f1fnttkc">{value}</p>
      <p className="mt-1 text-xs text-ink/50" data-id="element-1240" data-mp="1o24j12q8u0oq">{detail}</p>
    </article>;
}
function Legend({
  color,
  label
}: {
  color: string;
  label: string;
}) {
  return <span className="inline-flex items-center gap-2" data-id="element-1241" data-mp="2ftahleqbfqbw">
      <span className="h-2.5 w-2.5 rounded-full" style={{
      backgroundColor: color
    }} data-id="element-1242" data-mp="2fqocdlqwdmwu" />
      {label}
    </span>;
}
function FinanceTooltip({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
  }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return <div className="rounded-xl border border-ink/10 bg-paper px-3 py-2 text-xs shadow-lg" data-id="element-1243" data-mp="vdgp6irfi2zk">
      <p className="font-semibold text-ink" data-id="element-1244" data-mp="nt65in9qdltu">{label}</p>
      <ul className="mt-1.5 space-y-1" data-id="element-1245" data-mp="nrl90s3uvstc">
        {payload.map(entry => <li key={entry.name} className="flex items-center gap-2 text-ink/70" data-id="element-1246" data-mp="ita2aq8uxyfc">
            <span className="h-2 w-2 rounded-full" style={{
          backgroundColor: entry.color
        }} data-id="element-1247" data-mp="wucnz1vxx6pq" />
            <span data-id="element-1248" data-mp="ruhz13vq6r7b">{entry.name}</span>
            <span className="ml-auto font-semibold tabular-nums text-ink" data-id="element-1249" data-mp="rvja38cqrdp1">
              {entry.name === 'Operating margin' ? `${entry.value}%` : currency(Number(entry.value ?? 0))}
            </span>
          </li>)}
      </ul>
    </div>;
}